<template>
    <div ref="containerRef" class="verification-container">
        <input v-for="(item, index) in codeList" :key="index" class="verification-input" type="text"
            inputmode="numeric" autocomplete="one-time-code" maxlength="1" :value="item"
            @input="handleInput(index, $event)" @keydown="handleKeyDown(index, $event)"
            @paste="handlePaste(index, $event)" @focus="handleFocus" />
    </div>
</template>

<script>
import { ref, watch } from 'vue';

export default {
    name: 'VerificationCode',
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        length: {
            type: Number,
            default: 6
        }
    },
    emits: ['update:modelValue', 'complete'],
    setup(props, { emit }) {
        const containerRef = ref(null);
        const codeList = ref(Array.from({ length: props.length }, () => ''));

        const toList = value => {
            const chars = String(value || '')
                .slice(0, props.length)
                .split('');
            return Array.from({ length: props.length }, (_, index) => chars[index] || '');
        };

        // 外部赋值（初始化、登录失败后清空）时同步到每个格子
        watch(
            () => props.modelValue,
            value => {
                if (value === codeList.value.join('')) return;
                codeList.value = toList(value);
            },
            { immediate: true }
        );

        const emitValue = () => {
            const value = codeList.value.join('');
            emit('update:modelValue', value);
            if (value.length === props.length) emit('complete', value);
        };

        const onlyNumber = value => String(value || '').replace(/\D/g, '');

        const focusIndex = index => {
            const inputs = containerRef.value ? containerRef.value.querySelectorAll('.verification-input') : [];
            if (inputs[index]) inputs[index].focus();
        };

        // 从 start 开始依次填充，用于粘贴或一次输入多位
        const fillFrom = (start, value) => {
            value.split('').forEach((char, offset) => {
                const target = start + offset;
                if (target < props.length) codeList.value[target] = char;
            });
            const nextEmpty = codeList.value.findIndex(item => !item);
            if (nextEmpty >= 0) focusIndex(nextEmpty);
            emitValue();
        };

        const handleInput = (index, event) => {
            // 输入法组合过程中的中间态不处理，等组合结束后的 input 事件
            if (event.isComposing) return;

            const value = onlyNumber(event.target.value);
            if (value.length > 1) {
                event.target.value = '';
                fillFrom(index, value);
                return;
            }

            codeList.value[index] = value;
            // 被过滤掉非数字字符时，把输入框里的残留内容一并抹掉
            event.target.value = value;
            if (value && index < props.length - 1) focusIndex(index + 1);
            emitValue();
        };

        const handleKeyDown = (index, event) => {
            // 退格：当前格有值就清当前格，空的则清上一格并把焦点移回去
            if (event.key === 'Backspace') {
                event.preventDefault();
                if (codeList.value[index]) {
                    codeList.value[index] = '';
                } else if (index > 0) {
                    codeList.value[index - 1] = '';
                    focusIndex(index - 1);
                } else {
                    return;
                }
                emitValue();
                return;
            }

            // 单字符按键只放行数字，拦截字母与符号（控制键是多字符，不受影响）
            if (event.key.length === 1 && !/\d/.test(event.key) && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
            }
        };

        const handlePaste = (index, event) => {
            const text = onlyNumber(event.clipboardData ? event.clipboardData.getData('text') : '');
            if (!text) return;
            event.preventDefault();
            // 从当前所在格开始依次填充
            fillFrom(index, text);
        };

        // 聚焦时全选，方便直接覆盖输入
        const handleFocus = event => {
            event.target.select();
        };

        return {
            containerRef,
            codeList,
            handleInput,
            handleKeyDown,
            handlePaste,
            handleFocus
        };
    }
};
</script>

<style lang="scss" scoped>
.verification-container {
    display: flex;
    /* 聚焦光晕是向外扩散的（box-shadow 0 0 0 3px），给首位格子留出空间避免贴边被遮挡 */
    padding: 0 4px;
}

.verification-input {
    width: 40px;
    height: 40px;
    margin-right: 10px;
    text-align: center;
    font-size: 18px;
    border: 1px solid #ccc;
    border-radius: 5px;
}

.verification-input:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 5px #007bff;
}
</style>
